import { PROTECTED_CAPABILITY, isProtectedCapability } from "./capability.js";
/** How a conversation was confirmed (`party_confirmation_method`). */
export const PARTY_CONFIRMATION_METHOD = ["sms_code", "passcode", "signed_visitor", "number_recognition"];
/** The methods that can confirm anything (`party_confirmation.method`, 0188's CHECK) — every method but number recognition. */
export const CONFIRMING_METHOD = ["sms_code", "passcode", "signed_visitor"];
/** A VoiceAgent's minimum (`party_confirmation_requirement.minimum_method`, 0188's CHECK). */
export const PARTY_CONFIRMATION_MINIMUM = ["sms_code", "sms_code_or_signed_visitor", "signed_visitor", "passcode"];
export function isPartyConfirmationMethod(value) {
    return PARTY_CONFIRMATION_METHOD.includes(value);
}
export function isConfirmingMethod(value) {
    return CONFIRMING_METHOD.includes(value);
}
export function isPartyConfirmationMinimum(value) {
    return PARTY_CONFIRMATION_MINIMUM.includes(value);
}
/** The confirming methods each minimum accepts, in {@link CONFIRMING_METHOD}'s order (§40.8.2's table). */
const SATISFIED_BY = Object.freeze({
    passcode: Object.freeze(["sms_code", "passcode", "signed_visitor"]),
    sms_code: Object.freeze(["sms_code"]),
    sms_code_or_signed_visitor: Object.freeze(["sms_code", "signed_visitor"]),
    signed_visitor: Object.freeze(["signed_visitor"]),
});
/** The confirming methods that satisfy `minimum`. */
export function satisfyingMethodsOf(minimum) {
    return SATISFIED_BY[minimum];
}
/** Whether a conversation confirmed with `method` meets `minimum` (§40.8.2). Number recognition never does. */
export function methodSatisfies(minimum, method) {
    return isConfirmingMethod(method) && SATISFIED_BY[minimum].includes(method);
}
/** The strength order of the confirming methods (the record's upgrade): passcode < sms_code = signed_visitor. */
export const CONFIRMING_METHOD_STRENGTH = Object.freeze({
    passcode: 1,
    sms_code: 2,
    signed_visitor: 2,
});
/** Whether `method` is at least as strong as `than`. */
export function methodAtLeastAsStrong(method, than) {
    return CONFIRMING_METHOD_STRENGTH[method] >= CONFIRMING_METHOD_STRENGTH[than];
}
/**
 * The first rule a requirement breaks, or `null` (§40.19.5): a capability named twice or outside the closed list, a
 * capability the agent_template does not declare, an SMS minimum while codes are off, a signed-visitor-only minimum while
 * signed visitors are off or while a phone or SIP trunk binding is bound. The `passcode` minimum is always saveable (it
 * is met by any method; the portal shows its warning). Pure.
 */
export function partyConfirmationRequirementRefusalOf(requirement, context) {
    const seen = new Set();
    for (const capability of requirement.protectedCapability) {
        if (!isProtectedCapability(capability))
            return { code: "validation_failed", reason: "unknown_capability" };
        if (seen.has(capability))
            return { code: "validation_failed", reason: "duplicate_capability" };
        seen.add(capability);
    }
    if (!isPartyConfirmationMinimum(requirement.minimumMethod))
        return { code: "validation_failed", reason: "unknown_minimum" };
    for (const capability of requirement.protectedCapability) {
        if (!context.declaration.includes(capability))
            return { code: "capability_not_protectable", capability };
    }
    const minimum = requirement.minimumMethod;
    if ((minimum === "sms_code" || minimum === "sms_code_or_signed_visitor") && !context.smsCodeOn) {
        return { code: "method_unavailable", reason: "sms_code_off" };
    }
    if (minimum === "signed_visitor") {
        if (!context.signedVisitorOn)
            return { code: "method_unavailable", reason: "signed_visitor_off" };
        if (context.voiceBindingPresent)
            return { code: "method_unavailable", reason: "signed_visitor_voice_binding" };
    }
    return null;
}
/** The access level's implied minimum for a listed party (§40.8.2), or `null`. */
function accessMinimumOf(access) {
    if (access.kind !== "listed")
        return null;
    if (access.assuranceLevel === "number_sms_code")
        return "sms_code";
    if (access.assuranceLevel === "number_passcode")
        return "passcode";
    return null;
}
/**
 * The stint's effective requirement (§40.8.2), or `null` when nothing is protected: no row (or an empty one) and no
 * implied access requirement. Pure.
 */
export function effectiveRequirementOf(row, access) {
    const rowInForce = row !== null && row.protectedCapability.length > 0 ? row : null;
    const accessMinimum = accessMinimumOf(access);
    if (rowInForce === null && accessMinimum === null)
        return null;
    const minimumMethod = [];
    if (rowInForce !== null)
        minimumMethod.push(rowInForce.minimumMethod);
    if (accessMinimum !== null && !minimumMethod.includes(accessMinimum))
        minimumMethod.push(accessMinimum);
    const satisfiedBy = CONFIRMING_METHOD.filter((method) => minimumMethod.every((minimum) => SATISFIED_BY[minimum].includes(method)));
    const protectedCapability = rowInForce === null ? [] : PROTECTED_CAPABILITY.filter((capability) => rowInForce.protectedCapability.includes(capability));
    return Object.freeze({
        protectedCapability: Object.freeze(protectedCapability),
        everyTool: accessMinimum !== null,
        minimumMethod: Object.freeze(minimumMethod),
        satisfiedBy: Object.freeze(satisfiedBy),
    });
}
//# sourceMappingURL=method.js.map